import { forwardRef, useImperativeHandle, useRef } from 'react';
import { TranslationService } from '../../../services/translation.service';
import ValidationInfo from '../../../validation/validation-info';
import type IContactInformation from '../../../models/contact-information.model';
import type { IServerValidationError } from '../validatable.interface';
import TextFieldFormGroup from '../TextFieldFormGroup';
import type IValidatable from '../validatable.interface';
import { ContactInformationLookupField } from './contact-information-lookup-field.enum';
import PersonalIdentityNumberLookup from '../PersonalIdentityNumberLookup';
import { PersonalIdentityNumberFeature } from '../PersonalIdentityNumberLookup/personal-identity-lookup-feature.enum';

export interface IContactInformationLookupState {
  value: IContactInformation;
  validationInfo: ValidationInfo;
}

interface IContactInformationLookupProps {
  state: IContactInformationLookupState;
  feature: PersonalIdentityNumberFeature;
  noPersonalIdentityNumberText?: string;
  onChange?: (value: IContactInformation) => void;
  onKeyUp?: (field: ContactInformationLookupField) => void;
}

export default forwardRef<IValidatable, IContactInformationLookupProps>(function ContactInformationLookup(
  { state, feature, noPersonalIdentityNumberText, onChange, onKeyUp }: IContactInformationLookupProps,
  ref,
) {
  // === REFERENCES ===
  const phoneNumberRef = useRef<IValidatable>(null!);
  const emailRef = useRef<IValidatable>(null!);

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      return new ValidationInfo('', [], {
        phoneNumber: phoneNumberRef.current.checkValidity(),
        email: emailRef.current.checkValidity(),
      });
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo('', [], {
        phoneNumber: phoneNumberRef.current.handleServerValidationErrors(errors),
        email: emailRef.current.handleServerValidationErrors(errors),
      });
    },
  }));

  return (
    <div className="contact-information">
      <PersonalIdentityNumberLookup
        state={state}
        feature={feature}
        noPersonalIdentityNumberText={noPersonalIdentityNumberText}
        onChange={(value) => onChange?.({ ...value, phoneNumber: state.value.phoneNumber, email: state.value.email })}
      />
      {state.value.valid && (
        <div className="row">
          <TextFieldFormGroup
            ref={phoneNumberRef}
            inputId="contact-information-phone-number"
            serverValidationName="phoneNumber"
            labelText={TranslationService.get('common.phonenumber')}
            pattern="\+?[0-9]+"
            required={true}
            autocomplete="tel"
            type="tel"
            className="col-sm-6 mb-sm-0"
            state={{ value: state.value.phoneNumber, validationInfo: state.validationInfo.children.phoneNumber ?? new ValidationInfo() }}
            onChange={(value) => onChange?.({ ...state.value, phoneNumber: value })}
            onKeyUp={() => onKeyUp?.(ContactInformationLookupField.phoneNumber)}
          />
          <TextFieldFormGroup
            ref={emailRef}
            inputId="contact-information-email"
            serverValidationName="email"
            labelText={TranslationService.get('common.email')}
            required={true}
            autocomplete="email"
            type="email"
            className="col-sm-6 mb-sm-0"
            state={{ value: state.value.email, validationInfo: state.validationInfo.children.email ?? new ValidationInfo() }}
            onChange={(value) => onChange?.({ ...state.value, email: value })}
            onKeyUp={() => onKeyUp?.(ContactInformationLookupField.email)}
          />
        </div>
      )}
    </div>
  );
});
